/* 
authV2.js
*/

const STORAGE_KEY = "fm_v2_session";
const API_URL = process.env.REACT_APP_SERVER_URL;

// ── Storage layer ──────────────────────────────────────────────────────

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.token) return null;
    return obj;
  } catch {
    return null;
  }
};

const writeStored = (sess) => {
  if (sess) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sess));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  notify();
};

// ── Pub/Sub for live updates ───────────────────────────────────────────

const listeners = new Set();
const notify = () => {
  const sess = readStored();
  listeners.forEach((l) => {
    try { l(sess); } catch { /* swallow */ }
  });
};

// Listen for changes from OTHER tabs (e.g. logout in tab A should
// instantly log out tab B). The "storage" event only fires in tabs
// that aren't the originator of the change.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) notify();
  });
}

// ── Heartbeat — keep an active tab's session warm ──────────────────────

let heartbeatTimer = null;

const stopHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

const startHeartbeat = () => {
  stopHeartbeat();
  // Ping every 5 minutes. Server idle window defaults to 30 minutes, so
  // 5min gives a healthy 6× safety margin against missed beats.
  heartbeatTimer = setInterval(async () => {
    if (document.hidden) return; // don't keep dead tabs alive
    const sess = readStored();
    if (!sess?.token) { stopHeartbeat(); return; }
    try {
      const res = await fetch(`${API_URL}/v2/auth/heartbeat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sess.token}` },
      });
      if (res.status === 401) {
        console.warn("[auth] heartbeat → session invalid, clearing");
        writeStored(null);
        stopHeartbeat();
      }
    } catch { /* network blip, try next tick */ }
},1 * 60 * 1000); // every 1 minute};
};

// ── Public API ─────────────────────────────────────────────────────────

export const auth = {
  /** Read the current session blob synchronously. */
  getSession: readStored,

  /** Returns true if there's a session token cached locally. Does NOT
   *  guarantee the token is still valid server-side — use validate() or
   *  fetch() for that. */
  hasSession: () => !!readStored()?.token,

  /** Subscribe to session changes. Returns an unsubscribe function. */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** POST /auth/login. On success, stores the session and starts the
   *  heartbeat timer. Throws on failure with a user-friendly message. */
  async login(fdaId, password) {
    const res = await fetch(`${API_URL}/v2/auth/login`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ fdaId, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = typeof data.detail === "string"
        ? data.detail
        : "Sign-in failed. Please try again.";
      throw new Error(msg);
    }
    writeStored({
      token:              data.sessionToken,
      fdaId:              data.fdaId,
      username:           data.username,
      email:              data.email || null,
      idleTimeoutMinutes: data.idleTimeoutMinutes,
      loggedInAt:         Date.now(),
    });
    // Also pre-fill the legacy localStorage keys HomeV2 reads, so the
    // create form is auto-filled with the logged-in identity.
    if (data.username) localStorage.setItem("username", data.username);
    if (data.email)    localStorage.setItem("userEmail", data.email);
    localStorage.setItem("fdaId", data.fdaId);
    startHeartbeat();
    return data;
  },

  /** POST /auth/logout. Always clears local state, even if the API call
   *  fails (no point keeping a dead token around). */
  async logout(navigate) {
    const sess = readStored();
    if (sess?.token) {
      try {
        await fetch(`${API_URL}/v2/auth/logout`, {
          method:  "POST",
          headers: { Authorization: `Bearer ${sess.token}` },
        });
      } catch { /* best-effort */ }
    }
    writeStored(null);
    stopHeartbeat();
    if (navigate) navigate("/v2/login", { replace: true });
  },

  /** GET /auth/me — validates the current token against the server.
   *  Returns the merged user profile (including token) on success, null
   *  if the server explicitly rejected the token, or the cached session
   *  on transient errors so the user can keep working through a hiccup.
   *
   *  IMPORTANT: callers like HomeV2 use the return value to drive UI
   *  gates such as the Create-meeting form. The returned object MUST
   *  include the token, otherwise `session?.token` checks fail and the
   *  user appears signed out even though the session is fine. */
  async validate() {
    const sess = readStored();
    if (!sess?.token) return null;
    try {
      const res = await fetch(`${API_URL}/v2/auth/me`, {
        headers: { Authorization: `Bearer ${sess.token}` },
      });
      if (res.status === 401) {
        // Server explicitly rejected the token (single-session enforcement
        // kicked us out, session was wiped, etc.) — drop the local copy.
        writeStored(null);
        stopHeartbeat();
        return null;
      }
      if (!res.ok) {
        // Transient server error — DO NOT clear the local session, the
        // user is still authenticated as far as we can tell. They'll
        // get a 401 on the next real action if the session is actually
        // dead, and that path handles it correctly.
        return sess;
      }
      const data = await res.json();
      // Merge server-fresh profile fields onto the cached session, but
      // keep the original token (the server never returns the token in
      // /auth/me responses). Return the *merged* object so the caller
      // has a session that includes the token.
      const merged = { ...sess, ...data, token: sess.token };
      writeStored(merged);
      startHeartbeat();
      return merged;
    } catch {
      // Network error — keep using the cached session, let the user keep
      // working. Real-world this happens during transient connection
      // drops; we shouldn't be aggressive about kicking the user out.
      return sess;
    }
  },

  /** Use inside a page's useEffect to enforce "must be logged in". If
   *  there's no session, the user is redirected to /v2/login and the
   *  promise resolves with `null`. */
  async requireSession(navigate, redirectTo = "/v2/login") {
    const cached = readStored();
    if (!cached?.token) {
      navigate(redirectTo, { replace: true });
      return null;
    }
    // Validate in the background; if invalid, bounce.
    const verified = await this.validate();
    if (!verified) {
      navigate(redirectTo, { replace: true });
      return null;
    }
    return verified;
  },

  /** Authenticated fetch — adds the bearer header automatically.
   *  On 401 the local session is wiped and the request resolves with
   *  the original Response so callers can decide what to do. */
  async fetch(url, init = {}) {
    const sess = readStored();
    const headers = new Headers(init.headers || {});
    if (sess?.token) headers.set("Authorization", `Bearer ${sess.token}`);
    const res = await fetch(url, { ...init, headers });
    if (res.status === 401) {
      writeStored(null);
      stopHeartbeat();
    }
    return res;
  },
};

// Kick off the heartbeat at module load if we already have a session
// (covers full-page reloads where the helper file is re-evaluated).
if (typeof window !== "undefined" && readStored()?.token) {
  startHeartbeat();
}

export default auth;